import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Users, ShoppingCart, DollarSign, Eye, TrendingUp, Mic, LogOut, FileDown, UserPlus, Trash2, KeyRound, RefreshCw } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

import { API } from "@/lib/api";

function getAuthHeaders() {
  // httpOnly cookie is primary auth (set by backend). localStorage is fallback only.
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function api(path) {
  return axios.get(`${API}${path}`, { headers: getAuthHeaders(), withCredentials: true });
}

const GOLD = "#D4AF37";
const COLORS = ["#D4AF37", "#E5C07B", "#71717A", "#A1A1AA", "#3B82F6", "#8B5CF6", "#EF4444"];

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-[#121212] border border-white/10 p-5 rounded-sm" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-sm flex items-center justify-center">
          <Icon className="w-4 h-4 text-[#D4AF37]" />
        </div>
        <span className="text-xs tracking-[0.15em] uppercase text-zinc-500">{label}</span>
      </div>
      <div className="font-heading text-3xl text-white">{value}</div>
      {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
    </div>
  );
}

function DataTable({ columns, rows, title }) {
  return (
    <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
      {title && <div className="px-5 py-3 border-b border-white/10"><h3 className="font-heading text-lg text-white uppercase tracking-tight">{title}</h3></div>}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10">
              {columns.map((c) => (
                <th key={c.key} className="px-4 py-3 text-left text-[10px] tracking-[0.15em] uppercase text-zinc-500 font-medium">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-zinc-600 text-sm">No data yet</td></tr>
            )}
            {rows.map((row, i) => (
              <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                {columns.map((c) => (
                  <td key={c.key} className="px-4 py-3 text-zinc-300 text-xs">{c.render ? c.render(row[c.key], row) : row[c.key] || "—"}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function downloadCSV(data, filename) {
  if (!data.length) return;
  const keys = Object.keys(data[0]);
  const csv = [keys.join(","), ...data.map((r) => keys.map((k) => `"${String(r[k] || "").replace(/"/g, '""')}"`).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
}

const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [leadsTime, setLeadsTime] = useState([]);
  const [leads, setLeads] = useState([]);
  const [preorders, setPreorders] = useState([]);
  const [speaking, setSpeaking] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [salesBreakdown, setSalesBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminUsers, setAdminUsers] = useState([]);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showResetPw, setShowResetPw] = useState(null);
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "" });
  const [newPassword, setNewPassword] = useState("");

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [ov, tr, lt, ld, po, sp, tx, us, sb] = await Promise.all([
        api("/admin/analytics/overview"),
        api("/admin/analytics/traffic"),
        api("/admin/analytics/leads-over-time"),
        api("/admin/analytics/data/leads"),
        api("/admin/analytics/data/preorders"),
        api("/admin/analytics/data/speaking"),
        api("/admin/analytics/data/transactions"),
        api("/admin/users"),
        api("/admin/analytics/sales-breakdown"),
      ]);
      setOverview(ov.data);
      setTraffic(tr.data);
      setLeadsTime(lt.data);
      setLeads(ld.data);
      setPreorders(po.data);
      setSpeaking(sp.data);
      setTransactions(tx.data);
      setAdminUsers(us.data);
      setSalesBreakdown(sb.data);
    } catch (err) {
      console.error("Dashboard load failed:", err);
      navigate("/admin");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="bg-[#050505] min-h-screen flex items-center justify-center">
        <div className="text-[#D4AF37] font-heading text-2xl animate-pulse">Loading Dashboard...</div>
      </div>
    );
  }

  const o = overview || {};

  return (
    <div className="bg-[#050505] min-h-screen" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#050505] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="font-brand text-xl text-white">FYB</span>
            <Separator orientation="vertical" className="h-5 bg-white/10" />
            <span className="text-xs tracking-[0.15em] uppercase text-[#D4AF37]">Analytics Dashboard</span>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-zinc-500 text-xs hover:text-white transition-colors" data-testid="admin-logout-button">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-8">
          <StatCard icon={Users} label="Total Leads" value={o.total_leads || 0} sub={`Chapter: ${o.chapter_leads || 0} | Guide: ${o.guide_leads || 0}`} />
          <StatCard icon={ShoppingCart} label="Pre-Orders" value={o.total_preorders || 0} />
          <StatCard icon={DollarSign} label="Revenue" value={`$${(o.total_revenue || 0).toFixed(2)}`} sub={`${o.total_paid_orders || 0} paid orders`} />
          <StatCard icon={Eye} label="Page Views" value={o.total_page_views || 0} sub={`Unique: ${o.unique_visitors || 0} | Returning: ${o.returning_views || 0}`} />
          <StatCard icon={TrendingUp} label="Conversion" value={`${o.conversion_rate || 0}%`} sub="Leads → Pre-orders" />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="traffic" className="space-y-6">
          <TabsList className="bg-[#121212] border border-white/10 p-1 rounded-sm">
            <TabsTrigger value="traffic" className="text-xs tracking-wider uppercase data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#050505] rounded-sm" data-testid="tab-traffic">Traffic & Acquisition</TabsTrigger>
            <TabsTrigger value="leads" className="text-xs tracking-wider uppercase data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#050505] rounded-sm" data-testid="tab-leads">Leads & Opt-ins</TabsTrigger>
            <TabsTrigger value="orders" className="text-xs tracking-wider uppercase data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#050505] rounded-sm" data-testid="tab-orders">Orders & Revenue</TabsTrigger>
            <TabsTrigger value="speaking" className="text-xs tracking-wider uppercase data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#050505] rounded-sm" data-testid="tab-speaking">Speaking</TabsTrigger>
            <TabsTrigger value="users" className="text-xs tracking-wider uppercase data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#050505] rounded-sm" data-testid="tab-users">User Management</TabsTrigger>
          </TabsList>

          {/* TRAFFIC TAB */}
          <TabsContent value="traffic" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Daily Page Views Chart */}
              <div className="bg-[#121212] border border-white/10 p-6 rounded-sm">
                <h3 className="font-heading text-lg text-white uppercase tracking-tight mb-4">Page Views (Last 30 Days)</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={traffic?.daily_views || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="date" tick={{ fill: '#71717A', fontSize: 10 }} tickFormatter={(v) => v?.slice(5)} />
                    <YAxis tick={{ fill: '#71717A', fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, color: '#fff', fontSize: 12 }} />
                    <Legend />
                    <Bar dataKey="unique" fill={GOLD} name="Unique Visitors" stackId="views" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="returning" fill="#71717A" name="Returning Visitors" stackId="views" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Acquisition Sources */}
              <div className="bg-[#121212] border border-white/10 p-6 rounded-sm">
                <h3 className="font-heading text-lg text-white uppercase tracking-tight mb-4">Acquisition Sources</h3>
                {(traffic?.acquisition || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={traffic.acquisition} dataKey="count" nameKey="source" cx="50%" cy="50%" outerRadius={90} label={({ source, percent }) => `${source} ${(percent * 100).toFixed(0)}%`} labelLine={{ stroke: '#71717A' }}>
                        {traffic.acquisition.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, color: '#fff', fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-zinc-600 text-sm">No traffic data yet. Visits will appear here.</div>
                )}
              </div>
            </div>

            {/* Top Pages & Campaigns */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <DataTable
                title="Top Pages"
                columns={[
                  { key: "page", label: "Page" },
                  { key: "count", label: "Views" },
                ]}
                rows={traffic?.top_pages || []}
              />
              <DataTable
                title="Campaigns (UTM)"
                columns={[
                  { key: "campaign", label: "Campaign" },
                  { key: "count", label: "Visits" },
                ]}
                rows={traffic?.campaigns || []}
              />
            </div>

            {/* Acquisition bar chart */}
            {(traffic?.acquisition || []).length > 0 && (
              <div className="bg-[#121212] border border-white/10 p-6 rounded-sm">
                <h3 className="font-heading text-lg text-white uppercase tracking-tight mb-4">Traffic by Source</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={traffic.acquisition}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="source" tick={{ fill: '#71717A', fontSize: 10 }} />
                    <YAxis tick={{ fill: '#71717A', fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, color: '#fff', fontSize: 12 }} />
                    <Bar dataKey="count" fill={GOLD} radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </TabsContent>

          {/* LEADS TAB */}
          <TabsContent value="leads" className="space-y-6">
            {/* Leads over time chart */}
            <div className="bg-[#121212] border border-white/10 p-6 rounded-sm">
              <h3 className="font-heading text-lg text-white uppercase tracking-tight mb-4">Leads Over Time (Last 30 Days)</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={leadsTime}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" tick={{ fill: '#71717A', fontSize: 10 }} tickFormatter={(v) => v?.slice(5)} />
                  <YAxis tick={{ fill: '#71717A', fontSize: 10 }} />
                  <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, color: '#fff', fontSize: 12 }} />
                  <Legend />
                  <Bar dataKey="chapter" fill={GOLD} name="Free Chapter" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="guide" fill="#E5C07B" name="Mini-Guide" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Leads table */}
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading text-xl text-white uppercase tracking-tight">All Leads ({leads.length})</h3>
              <button onClick={() => downloadCSV(leads, "fyb_leads")} className="flex items-center gap-2 text-[#D4AF37] text-xs tracking-wider uppercase hover:text-[#E5C07B] transition-colors" data-testid="export-leads-csv">
                <FileDown className="w-4 h-4" /> Export CSV
              </button>
            </div>
            <DataTable
              columns={[
                { key: "email", label: "Email" },
                { key: "name", label: "Name" },
                { key: "lead_type", label: "Type", render: (v) => <span className={`px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider font-bold ${v === 'chapter' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'bg-[#E5C07B]/10 text-[#E5C07B]'}`}>{v}</span> },
                { key: "created_at", label: "Date", render: formatDate },
              ]}
              rows={leads}
            />
          </TabsContent>

          {/* ORDERS TAB */}
          <TabsContent value="orders" className="space-y-6">
            {/* Sync with Stripe */}
            <div className="bg-[#121212] border border-[#D4AF37]/30 p-5 rounded-sm flex items-center justify-between">
              <div>
                <h3 className="font-heading text-lg text-white uppercase tracking-tight">Stripe Sync</h3>
                <p className="text-zinc-500 text-xs mt-1">Pull customer emails from Stripe, update payment statuses, and mark abandoned carts.</p>
              </div>
              <Button
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API}/admin/sync-stripe`, {}, { headers: getAuthHeaders(), withCredentials: true });
                    toast.success(res.data.message);
                    loadAll();
                  } catch {
                    toast.error("Sync failed. Please try again.");
                  }
                }}
                className="btn-gold h-10 px-6 rounded-sm text-xs tracking-[0.1em] uppercase"
                data-testid="sync-stripe-button"
              >
                Sync with Stripe
              </Button>
            </div>

            {/* Sales Breakdown */}
            {salesBreakdown && (
              <div className="bg-[#121212] border border-[#D4AF37]/30 p-6 rounded-sm space-y-4" data-testid="sales-breakdown">
                <h3 className="font-heading text-lg text-white uppercase tracking-tight">Sales Breakdown</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {salesBreakdown.breakdown.map((item) => (
                    <div key={item.package_id} className="bg-[#050505]/50 border border-white/10 p-4 rounded-sm text-center">
                      <p className="text-[10px] tracking-[0.15em] uppercase text-[#D4AF37] font-bold mb-2">{item.package_name}</p>
                      <div className="font-heading text-3xl text-white">{item.quantity}</div>
                      <p className="text-xs text-zinc-500 mt-1">{item.orders} order{item.orders !== 1 ? "s" : ""}</p>
                      <p className="text-xs text-[#D4AF37] font-semibold mt-1">${item.revenue.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between border-t border-white/10 pt-4 mt-4">
                  <div className="flex gap-6">
                    <div>
                      <span className="text-xs text-zinc-500 uppercase tracking-wider">Total Items Sold</span>
                      <p className="font-heading text-2xl text-white">{salesBreakdown.totals.total_quantity}</p>
                    </div>
                    <div>
                      <span className="text-xs text-zinc-500 uppercase tracking-wider">Total Revenue</span>
                      <p className="font-heading text-2xl text-[#D4AF37]">${salesBreakdown.totals.total_revenue.toFixed(2)} AUD</p>
                    </div>
                  </div>
                  <div className="flex gap-4 text-xs">
                    <span className="text-zinc-500">Abandoned: <span className="text-red-400 font-semibold">{salesBreakdown.abandoned_carts}</span></span>
                    <span className="text-zinc-500">Pending: <span className="text-yellow-400 font-semibold">{salesBreakdown.pending_carts}</span></span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mb-2">
              <button onClick={() => downloadCSV(preorders, "fyb_preorders")} className="flex items-center gap-2 text-[#D4AF37] text-xs tracking-wider uppercase hover:text-[#E5C07B] transition-colors" data-testid="export-preorders-csv">
                <FileDown className="w-4 h-4" /> Export CSV
              </button>
            </div>
            <DataTable
              columns={[
                { key: "email", label: "Email" },
                { key: "name", label: "Name" },
                { key: "price_tier", label: "Tier", render: (v) => <span className="px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider font-bold bg-[#D4AF37]/10 text-[#D4AF37]">{v}</span> },
                { key: "created_at", label: "Date", render: formatDate },
              ]}
              rows={preorders}
            />

            <div className="flex items-center justify-between mb-2 mt-8">
              <h3 className="font-heading text-xl text-white uppercase tracking-tight">Payment Transactions ({transactions.length})</h3>
              <button onClick={() => downloadCSV(transactions, "fyb_transactions")} className="flex items-center gap-2 text-[#D4AF37] text-xs tracking-wider uppercase hover:text-[#E5C07B] transition-colors" data-testid="export-transactions-csv">
                <FileDown className="w-4 h-4" /> Export CSV
              </button>
            </div>
            <DataTable
              columns={[
                { key: "email", label: "Email" },
                { key: "package_name", label: "Product" },
                { key: "amount", label: "Amount", render: (v) => `$${(v || 0).toFixed(2)}` },
                { key: "payment_status", label: "Status", render: (v) => <span className={`px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider font-bold ${v === 'paid' ? 'bg-green-900/30 text-green-400' : v === 'abandoned' ? 'bg-red-900/30 text-red-400' : 'bg-yellow-900/30 text-yellow-400'}`}>{v}</span> },
                { key: "created_at", label: "Date", render: formatDate },
              ]}
              rows={transactions}
            />
          </TabsContent>

          {/* SPEAKING TAB */}
          <TabsContent value="speaking" className="space-y-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading text-xl text-white uppercase tracking-tight">Speaking Inquiries ({speaking.length})</h3>
              <button onClick={() => downloadCSV(speaking, "fyb_speaking")} className="flex items-center gap-2 text-[#D4AF37] text-xs tracking-wider uppercase hover:text-[#E5C07B] transition-colors" data-testid="export-speaking-csv">
                <FileDown className="w-4 h-4" /> Export CSV
              </button>
            </div>
            <DataTable
              columns={[
                { key: "name", label: "Name" },
                { key: "email", label: "Email" },
                { key: "organization", label: "Organization" },
                { key: "message", label: "Message" },
                { key: "created_at", label: "Date", render: formatDate },
              ]}
              rows={speaking}
            />
          </TabsContent>

          {/* USERS TAB */}
          <TabsContent value="users" className="space-y-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading text-xl text-white uppercase tracking-tight">Admin Users ({adminUsers.length})</h3>
              <Button
                onClick={() => { setNewUser({ email: "", password: "", name: "" }); setShowCreateUser(true); }}
                className="btn-gold h-9 px-4 rounded-sm text-xs tracking-[0.1em] uppercase flex items-center gap-2"
                data-testid="create-user-button"
              >
                <UserPlus className="w-4 h-4" /> Add User
              </Button>
            </div>

            <div className="space-y-3">
              {adminUsers.map((u) => (
                <div key={u.id} className="bg-[#121212] border border-white/10 p-5 rounded-sm flex items-center justify-between" data-testid={`user-row-${u.email}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center">
                      <Users className="w-4 h-4 text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{u.email}</p>
                      <div className="flex items-center gap-3 mt-0.5">
                        {u.name && <span className="text-zinc-500 text-xs">{u.name}</span>}
                        <span className="text-[10px] tracking-wider uppercase text-[#D4AF37] border border-[#D4AF37]/30 px-2 py-0.5 rounded-sm">{u.role}</span>
                        {u.created_at && <span className="text-zinc-600 text-xs">{formatDate(u.created_at)}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => { setShowResetPw(u); setNewPassword(""); }}
                      className="h-8 px-3 rounded-sm text-[10px] tracking-wider uppercase border border-white/20 bg-transparent text-zinc-400 hover:text-white hover:border-[#D4AF37]"
                      data-testid={`reset-pw-${u.email}`}
                    >
                      <KeyRound className="w-3 h-3 mr-1" /> Reset Password
                    </Button>
                    <Button
                      onClick={async () => {
                        if (!window.confirm(`Delete user ${u.email}?`)) return;
                        try {
                          await axios.delete(`${API}/admin/users/${u.id}`, { headers: getAuthHeaders(), withCredentials: true });
                          setAdminUsers(adminUsers.filter((x) => x.id !== u.id));
                          toast.success(`User ${u.email} deleted`);
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Cannot delete user");
                        }
                      }}
                      className="h-8 px-3 rounded-sm text-[10px] tracking-wider uppercase border border-red-900/30 bg-transparent text-red-400 hover:bg-red-900/20 hover:border-red-600"
                      data-testid={`delete-user-${u.email}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Create User Dialog */}
            <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
              <DialogContent className="bg-[#121212] border border-[#D4AF37]/30 max-w-md" data-testid="create-user-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl tracking-tight uppercase text-white">Create New User</DialogTitle>
                  <DialogDescription className="text-zinc-400 text-sm">Add a new admin user to the dashboard.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <Input
                    type="text"
                    placeholder="Name"
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-11"
                    data-testid="new-user-name"
                  />
                  <Input
                    type="email"
                    placeholder="Email address"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-11"
                    data-testid="new-user-email"
                  />
                  <Input
                    type="password"
                    placeholder="Password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-11"
                    data-testid="new-user-password"
                  />
                  <Button
                    onClick={async () => {
                      if (!newUser.email || !newUser.password) { toast.error("Email and password are required"); return; }
                      try {
                        const res = await axios.post(`${API}/admin/users`, newUser, { headers: getAuthHeaders(), withCredentials: true });
                        setAdminUsers([...adminUsers, { ...res.data, created_at: new Date().toISOString() }]);
                        setShowCreateUser(false);
                        toast.success(`User ${res.data.email} created`);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Failed to create user");
                      }
                    }}
                    className="w-full btn-gold h-11 rounded-sm text-xs tracking-[0.15em] uppercase"
                    data-testid="confirm-create-user"
                  >
                    Create User
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Reset Password Dialog */}
            <Dialog open={!!showResetPw} onOpenChange={() => setShowResetPw(null)}>
              <DialogContent className="bg-[#121212] border border-[#D4AF37]/30 max-w-md" data-testid="reset-password-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl tracking-tight uppercase text-white">Reset Password</DialogTitle>
                  <DialogDescription className="text-zinc-400 text-sm">
                    Set a new password for <span className="text-white font-medium">{showResetPw?.email}</span>
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <Input
                    type="password"
                    placeholder="New password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-11"
                    data-testid="new-password-input"
                  />
                  <Button
                    onClick={async () => {
                      if (!newPassword) { toast.error("Password is required"); return; }
                      try {
                        await axios.post(`${API}/admin/users/reset-password`, { user_id: showResetPw.id, new_password: newPassword }, { headers: getAuthHeaders(), withCredentials: true });
                        setShowResetPw(null);
                        toast.success(`Password reset for ${showResetPw.email}`);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Failed to reset password");
                      }
                    }}
                    className="w-full btn-gold h-11 rounded-sm text-xs tracking-[0.15em] uppercase"
                    data-testid="confirm-reset-password"
                  >
                    Reset Password
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
